import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { stripChapterPrefix } from '../lib/utils';
import RichTextEditor from '../components/RichTextEditor';
import VolumeManager from '../components/VolumeManager';

interface DashboardPageProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

export default function DashboardPage({ onNavigate, user }: DashboardPageProps) {
  const [stories, setStories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewStory, setShowNewStory] = useState(false);
  const [showNewChapter, setShowNewChapter] = useState<number | null>(null);
  const [editingChapter, setEditingChapter] = useState<{ id: number; storyId: number } | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsStoryId, setAnalyticsStoryId] = useState<number | null>(null);
  const [penNames, setPenNames] = useState<any[]>([]);
  const [form, setForm] = useState({ title: '', synopsis: '', genre: 'Action', tags: '', coverUrl: '', isMature: false, penNameId: '' });
  const [chapterForm, setChapterForm] = useState({ title: '', content: '', authorNote: '', isScheduled: false, scheduledAt: '', isDraft: false });
  const [dragChapterId, setDragChapterId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'story' | 'chapter'; id: number; title: string } | null>(null);
  const [expandedChapters, setExpandedChapters] = useState<number | null>(null);
  const [editingStory, setEditingStory] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ title: '', synopsis: '', genre: '', tags: '', coverUrl: '', status: 'ONGOING', isMature: false, penNameId: '' });
  const [editCoverPreview, setEditCoverPreview] = useState<string | null>(null);
  const [editCoverError, setEditCoverError] = useState<string | null>(null);
  const [editCoverFile, setEditCoverFile] = useState<File | null>(null);
  const [editCoverUploading, setEditCoverUploading] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState<'overview' | 'traffic' | 'demographics' | 'chapters'>('overview');
  const [followersStoryId, setFollowersStoryId] = useState<number | null>(null);
  const [followers, setFollowers] = useState<any[]>([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState<any>(null);
  const [volumeManagerStoryId, setVolumeManagerStoryId] = useState<number | null>(null);

  // Writing Goals & Streak state
  const [writingGoal, setWritingGoal] = useState<number>(() => {
    const saved = localStorage.getItem('scribehaven_writing_goal');
    return saved ? Number(saved) : 500;
  });
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [todayWordCount, setTodayWordCount] = useState<number>(0);
  const [writingStreak, setWritingStreak] = useState<number>(0);

  const handleCoverFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setCoverError(null);
    if (!file) {
      setCoverPreview(null);
      setCoverFile(null);
      setForm(f => ({ ...f, coverUrl: '' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCoverError('File exceeds 5MB limit. Please choose a smaller image.');
      e.target.value = '';
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      setCoverError('Only JPG, PNG, GIF, and WebP images are allowed.');
      e.target.value = '';
      return;
    }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleEditCoverFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setEditCoverError(null);
    if (!file) {
      setEditCoverPreview(null);
      setEditCoverFile(null);
      setEditForm(f => ({ ...f, coverUrl: '' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setEditCoverError('File exceeds 5MB limit. Please choose a smaller image.');
      e.target.value = '';
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      setEditCoverError('Only JPG, PNG, GIF, and WebP images are allowed.');
      e.target.value = '';
      return;
    }
    setEditCoverFile(file);
    setEditCoverPreview(URL.createObjectURL(file));
  };

  const startEditStory = (story: any) => {
    setEditingStory(story.id);
    setEditForm({
      title: story.title || '',
      synopsis: story.synopsis || '',
      genre: story.genre || 'Action',
      tags: story.tags ? story.tags.join(', ') : '',
      coverUrl: story.coverUrl || '',
      status: story.status || 'ONGOING',
      isMature: story.isMature || false,
      penNameId: story.penName?.id?.toString() || '',
    });
    setEditCoverPreview(story.coverUrl || null);
    setEditCoverError(null);
  };

  const handleUpdateStory = async (storyId: number) => {
    setSubmitting(true);
    try {
      // Upload new cover to R2 if a file was selected
      let coverUrl = editForm.coverUrl;
      if (editCoverFile) {
        setEditCoverUploading(true);
        try {
          const result = await api.upload.cover(editCoverFile);
          coverUrl = result.url;
        } catch (err: any) {
          setEditCoverError(err.message || 'Failed to upload cover image.');
          setSubmitting(false);
          setEditCoverUploading(false);
          return;
        }
        setEditCoverUploading(false);
      }

      const tags = editForm.tags ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean).slice(0, 8) : [];
      const payload: any = {
        title: editForm.title,
        synopsis: editForm.synopsis,
        genre: editForm.genre,
        tags,
        coverUrl,
        status: editForm.status,
        isMature: editForm.isMature,
      };
      if (editForm.penNameId) {
        payload.penNameId = Number(editForm.penNameId);
      } else {
        payload.penNameId = null;
      }
      await api.stories.update(storyId, payload);
      const refreshed = await api.dashboard.stories();
      setStories(refreshed);
      setEditingStory(null);
      setEditCoverPreview(null);
      setEditCoverFile(null);
      setEditCoverError(null);
    } catch (err) {
      console.error('Update story failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    api.dashboard.stories()
      .then(data => {
        setStories(data);
        // Calculate today's word count from chapters published today
        const today = new Date().toDateString();
        let todayWords = 0;
        data.forEach((story: any) => {
          story.chapters?.forEach((ch: any) => {
            const publishedToday = ch.publishedAt && new Date(ch.publishedAt).toDateString() === today;
            if (publishedToday) todayWords += ch.wordCount || 0;
          });
        });
        setTodayWordCount(todayWords);
        // Update writing streak in localStorage
        const streakKey = 'scribehaven_writing_streak';
        const lastWriteKey = 'scribehaven_last_write_date';
        const lastWrite = localStorage.getItem(lastWriteKey);
        const storedStreak = Number(localStorage.getItem(streakKey) || '0');
        if (todayWords > 0) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          if (!lastWrite || lastWrite === yesterday.toDateString() || lastWrite === today) {
            const newStreak = lastWrite === today ? storedStreak : storedStreak + 1;
            localStorage.setItem(streakKey, String(newStreak));
            localStorage.setItem(lastWriteKey, today);
            setWritingStreak(newStreak);
          } else {
            // Streak broken
            localStorage.setItem(streakKey, '1');
            localStorage.setItem(lastWriteKey, today);
            setWritingStreak(1);
          }
        } else {
          setWritingStreak(storedStreak);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    api.penNames.list()
      .then(setPenNames)
      .catch(console.error);
    api.dashboard.recentActivity()
      .then(setRecentActivity)
      .catch(console.error);
  }, [user]);

  const handleCreateStory = async () => {
    setSubmitting(true);
    try {
      // Upload cover to R2 if a file was selected
      let coverUrl = form.coverUrl;
      if (coverFile) {
        setCoverUploading(true);
        try {
          const result = await api.upload.cover(coverFile);
          coverUrl = result.url;
        } catch (err: any) {
          setCoverError(err.message || 'Failed to upload cover image.');
          setSubmitting(false);
          setCoverUploading(false);
          return;
        }
        setCoverUploading(false);
      }

      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean).slice(0, 8) : [];
      const { penNameId: rawPenNameId, coverUrl: _oldCoverUrl, ...rest } = form;
      const payload: any = { ...rest, tags, coverUrl };
      if (rawPenNameId) {
        payload.penNameId = Number(rawPenNameId);
      }
      const story = await api.stories.create(payload);
      setStories(prev => [story, ...prev]);
      setShowNewStory(false);
      setForm({ title: '', synopsis: '', genre: 'Action', tags: '', coverUrl: '', isMature: false, penNameId: '' });
      setCoverPreview(null);
      setCoverFile(null);
      setCoverError(null);
    } catch (err) {
      console.error('Create story failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateChapter = async (overrideDraft?: boolean) => {
    if (!showNewChapter) return;
    const draft = overrideDraft !== undefined ? overrideDraft : chapterForm.isDraft;
    setSubmitting(true);
    try {
      await api.chapters.create({
        storyId: showNewChapter,
        title: chapterForm.title,
        content: chapterForm.content,
        authorNote: chapterForm.authorNote || null,
        isDraft: draft,
        scheduledAt: chapterForm.isScheduled && chapterForm.scheduledAt ? new Date(chapterForm.scheduledAt).toISOString() : null,
      });
      setShowNewChapter(null);
      setChapterForm({ title: '', content: '', authorNote: '', isScheduled: false, scheduledAt: '', isDraft: false });
      const refreshed = await api.dashboard.stories();
      setStories(refreshed);
    } catch (err) {
      console.error('Create chapter failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditChapter = async (chapterId: number, storyId: number) => {
    try {
      const chapter = await api.chapters.get(chapterId);
      setEditingChapter({ id: chapterId, storyId });
      setShowNewChapter(storyId);
      setChapterForm({
        title: chapter.title || '',
        content: chapter.content || '',
        authorNote: chapter.authorNote || '',
        isDraft: chapter.isDraft || false,
        isScheduled: !!chapter.scheduledAt,
        scheduledAt: chapter.scheduledAt ? new Date(chapter.scheduledAt).toISOString().slice(0, 16) : '',
      });
    } catch (err) {
      console.error('Load chapter for editing failed:', err);
    }
  };

  const handleUpdateChapter = async (overrideDraft?: boolean) => {
    if (!editingChapter) return;
    const draft = overrideDraft !== undefined ? overrideDraft : chapterForm.isDraft;
    setSubmitting(true);
    try {
      await api.chapters.update(editingChapter.id, {
        title: chapterForm.title,
        content: chapterForm.content,
        authorNote: chapterForm.authorNote || null,
        isDraft: draft,
        scheduledAt: chapterForm.isScheduled && chapterForm.scheduledAt ? new Date(chapterForm.scheduledAt).toISOString() : null,
      });
      setEditingChapter(null);
      setShowNewChapter(null);
      setChapterForm({ title: '', content: '', authorNote: '', isScheduled: false, scheduledAt: '', isDraft: false });
      const refreshed = await api.dashboard.stories();
      setStories(refreshed);
    } catch (err) {
      console.error('Update chapter failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const viewFollowers = async (storyId: number) => {
    if (followersStoryId === storyId) {
      setFollowersStoryId(null);
      setFollowers([]);
      return;
    }
    setFollowersLoading(true);
    try {
      const data = await api.dashboard.followers(storyId);
      setFollowers(data);
      setFollowersStoryId(storyId);
    } catch (err) {
      console.error('Followers failed:', err);
    } finally {
      setFollowersLoading(false);
    }
  };

  const viewAnalytics = async (storyId: number) => {
    if (analyticsStoryId === storyId) {
      setAnalyticsStoryId(null);
      setAnalytics(null);
      return;
    }
    try {
      const data = await api.dashboard.analytics(storyId);
      setAnalytics(data);
      setAnalyticsStoryId(storyId);
    } catch (err) {
      console.error('Analytics failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSubmitting(true);
    try {
      if (deleteConfirm.type === 'story') {
        await api.stories.delete(deleteConfirm.id);
        setStories(prev => prev.filter(s => s.id !== deleteConfirm.id));
        if (analyticsStoryId === deleteConfirm.id) {
          setAnalyticsStoryId(null);
          setAnalytics(null);
        }
      } else {
        await api.chapters.delete(deleteConfirm.id);
        const refreshed = await api.dashboard.stories();
        setStories(refreshed);
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Sign in to access your dashboard</h2>
        <button onClick={() => onNavigate('login')} className="px-6 py-3 bg-ink-600 rounded-xl font-semibold">
          Sign In
        </button>
      </div>
    );
  }

  const plainText = chapterForm.content.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  const chapterWordCount = plainText ? plainText.split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Author Dashboard</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('pen-names')}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-xl text-sm font-medium transition-colors"
          >
            Manage Pen Names
          </button>
          {!showNewChapter && (
            <button
              onClick={() => setShowNewStory(true)}
              className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
            >
              + New Story
            </button>
          )}
        </div>
      </div>

      {showNewStory && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-bold mb-4">Create New Story</h2>
          <div className="space-y-4">
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Story Title"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
            />
            <textarea
              value={form.synopsis}
              onChange={e => setForm(f => ({ ...f, synopsis: e.target.value }))}
              placeholder="Synopsis"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 h-24 resize-none focus:border-ink-500 focus:outline-none"
            />
            <div className="grid grid-cols-2 gap-4">
              <select
                value={form.genre}
                onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}
                className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none"
              >
                {['Action', 'Adventure', 'Comedy', 'Crime', 'Dark Fantasy', 'Drama', 'Dystopian', 'Erotica', 'Fanfiction', 'Fantasy', 'Historical', 'Horror', 'LGBTQ+', 'LitRPG', 'Mystery', 'New Adult', 'Paranormal', 'Poetry', 'Progression Fantasy', 'Romance', 'Romantasy', 'Sci-Fi', 'Short Story', 'Slice of Life', 'Thriller', 'Urban Fantasy', 'Werewolf', 'Western', 'Young Adult'].map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              <div>
                <input
                  value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="Tags (comma-separated)"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean).length : 0}/8 tags
                  {form.tags && form.tags.split(',').map(t => t.trim()).filter(Boolean).length > 8 && (
                    <span className="text-amber-400 ml-1">(only first 8 will be used)</span>
                  )}
                </p>
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Suggested tags:</p>
                  <div className="flex flex-wrap gap-1">
                    {[
                      { label: 'Romance', tags: ['Enemies to Lovers', 'Slow Burn', 'Second Chance', 'Forbidden Love', 'Billionaire', 'CEO', 'Reverse Harem'] },
                      { label: 'Fantasy', tags: ['Werewolf', 'Alpha', 'Vampire', 'Shifter', 'Fated Mates', 'Rejected Mate', 'Dark Romance'] },
                      { label: 'Subgenre', tags: ['Mafia', 'Paranormal', 'Urban', 'Historical', 'Dystopian', 'Academy', 'Reincarnation'] },
                    ].flatMap(cat => cat.tags).map(tag => {
                      const currentTags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
                      const isAdded = currentTags.some(t => t.toLowerCase() === tag.toLowerCase());
                      return (
                        <button
                          key={tag}
                          type="button"
                          disabled={isAdded || currentTags.length >= 8}
                          onClick={() => {
                            const newTags = form.tags ? form.tags + ', ' + tag : tag;
                            setForm(f => ({ ...f, tags: newTags }));
                          }}
                          className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                            isAdded
                              ? 'bg-ink-600/20 border-ink-500/50 text-ink-400 cursor-default'
                              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-ink-500/50 hover:text-ink-400'
                          } disabled:opacity-40`}
                        >
                          + {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            {penNames.length > 0 && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Publish as</label>
                <select
                  value={form.penNameId}
                  onChange={e => setForm(f => ({ ...f, penNameId: e.target.value }))}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none w-full"
                >
                  <option value="">{user?.username} (default)</option>
                  {penNames.map((pn: any) => (
                    <option key={pn.id} value={pn.id}>{pn.penName}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Cover Image (optional, max 2MB)</label>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleCoverFile}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-ink-600 file:text-white file:text-sm file:cursor-pointer focus:outline-none"
                  />
                  {coverError && (
                    <p className="text-red-400 text-sm mt-1">{coverError}</p>
                  )}
                </div>
                {coverPreview && (
                  <div className="relative shrink-0">
                    <img src={coverPreview} alt="Cover preview" className="w-20 h-28 object-cover rounded-lg border border-gray-700" />
                    <button
                      type="button"
                      onClick={() => { setCoverPreview(null); setForm(f => ({ ...f, coverUrl: '' })); setCoverError(null); }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`relative w-11 h-6 rounded-full transition-colors ${form.isMature ? 'bg-red-600' : 'bg-gray-700'}`}>
                  <input
                    type="checkbox"
                    checked={form.isMature}
                    onChange={e => setForm(f => ({ ...f, isMature: e.target.checked }))}
                    className="sr-only"
                  />
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.isMature ? 'translate-x-5' : ''}`} />
                </div>
                <div>
                  <span className={`text-sm font-medium ${form.isMature ? 'text-red-400' : 'text-gray-400'}`}>
                    🔞 18+ Content Warning
                  </span>
                  <p className="text-xs text-gray-500">Mark this story as containing mature content</p>
                </div>
              </label>
              <div className="flex gap-3">
                <button onClick={() => setShowNewStory(false)} className="px-4 py-2 text-gray-400 hover:text-white">
                  Cancel
                </button>
                <button
                  onClick={handleCreateStory}
                  disabled={!form.title || !form.synopsis || submitting}
                  className="px-6 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg font-medium disabled:opacity-50"
                >
                  Create Story
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Writing Goals & Streak Widget */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-400">Today's Writing Goal</h3>
          <div className="flex items-center gap-2">
            {writingStreak > 0 && (
              <span className="flex items-center gap-1 text-amber-400 text-sm font-medium">
                🔥 {writingStreak}-day streak
              </span>
            )}
            <button
              onClick={() => { setEditingGoal(true); setGoalInput(String(writingGoal)); }}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              Edit Goal
            </button>
          </div>
        </div>
        {editingGoal ? (
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={goalInput}
              onChange={e => setGoalInput(e.target.value)}
              className="w-32 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:border-ink-500 focus:outline-none"
              min="100"
              max="10000"
              step="100"
            />
            <span className="text-sm text-gray-400">words / day</span>
            <button
              onClick={() => {
                const g = Math.max(100, Math.min(10000, Number(goalInput) || 500));
                setWritingGoal(g);
                localStorage.setItem('scribehaven_writing_goal', String(g));
                setEditingGoal(false);
              }}
              className="px-3 py-1.5 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium"
            >
              Save
            </button>
            <button onClick={() => setEditingGoal(false)} className="text-sm text-gray-500 hover:text-gray-300">
              Cancel
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-end justify-between mb-2">
              <span className="text-2xl font-bold">{todayWordCount.toLocaleString()}</span>
              <span className="text-sm text-gray-500">/ {writingGoal.toLocaleString()} words</span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  todayWordCount >= writingGoal ? 'bg-emerald-500' : 'bg-ink-500'
                }`}
                style={{ width: `${Math.min(100, (todayWordCount / writingGoal) * 100)}%` }}
              />
            </div>
            {todayWordCount >= writingGoal ? (
              <p className="text-xs text-emerald-400 mt-2">🎉 Goal reached! Great work today.</p>
            ) : (
              <p className="text-xs text-gray-500 mt-2">
                {(writingGoal - todayWordCount).toLocaleString()} words to go
              </p>
            )}
          </div>
        )}
      </div>

      {recentActivity?.recentFollows?.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Recent Follower Activity</h3>
          <div className="space-y-2">
            {recentActivity.recentFollows.slice(0, 8).map((f: any, i: number) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-7 h-7 rounded-full bg-ink-600/30 flex items-center justify-center text-xs font-bold shrink-0">
                  {f.avatarUrl ? (
                    <img src={f.avatarUrl} alt={f.username} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    f.username[0].toUpperCase()
                  )}
                </div>
                <span className="text-gray-200">
                  <button onClick={() => onNavigate('profile', { username: f.username })} className="text-ink-400 hover:underline font-medium">{f.username}</button>
                  {' '}followed{' '}
                  <button onClick={() => onNavigate('story', { id: f.storyId })} className="text-ink-400 hover:underline font-medium">{f.storyTitle}</button>
                </span>
                <span className="text-xs text-gray-500 ml-auto shrink-0">
                  {new Date(f.followedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-20 bg-gray-900/50 rounded-2xl border border-gray-800">
          <span className="text-5xl block mb-4">✍️</span>
          <h2 className="text-2xl font-bold mb-2">No stories yet</h2>
          <p className="text-gray-400">Create your first story and start writing!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {stories.map(story => (
            <div key={story.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold">{story.title}</h3>
                      {story.penName && (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-purple-900/30 text-purple-400">
                          by {story.penName.penName}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        story.status === 'ONGOING' ? 'bg-emerald-900/30 text-emerald-400'
                        : story.status === 'COMPLETED' ? 'bg-blue-900/30 text-blue-400'
                        : 'bg-amber-900/30 text-amber-400'
                      }`}>
                        {story.status}
                      </span>
                    </div>
                    <div className="flex gap-4 text-sm text-gray-400">
                      <span>{story.genre}</span>
                      <span>{story._count?.chapters || 0} chapters</span>
                      <span>{story._count?.follows || 0} followers</span>
                      <span>{story.totalViews} views</span>
                    </div>
                    {story.chapters?.filter((ch: any) => ch.scheduledAt && new Date(ch.scheduledAt) > new Date()).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {story.chapters.filter((ch: any) => ch.scheduledAt && new Date(ch.scheduledAt) > new Date()).map((ch: any) => (
                          <span key={ch.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-900/20 text-amber-400 rounded-full text-xs">
                            🕐 Ch.{ch.chapterNum} — {new Date(ch.scheduledAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => startEditStory(story)}
                    className="px-4 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-medium transition-colors"
                  >
                    Edit Story
                  </button>
                  <button
                    onClick={() => { setEditingChapter(null); setShowNewChapter(story.id); setChapterForm({ title: '', content: '', authorNote: '', isScheduled: false, scheduledAt: '', isDraft: false }); }}
                    className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-sm font-medium transition-colors"
                  >
                    + Chapter
                  </button>
                  <button
                    onClick={() => viewAnalytics(story.id)}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    {analyticsStoryId === story.id ? 'Hide' : 'Analytics'}
                  </button>
                  <button
                    onClick={() => viewFollowers(story.id)}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    {followersStoryId === story.id ? 'Hide' : `Followers (${story._count?.follows || 0})`}
                  </button>
                  <button
                    onClick={() => onNavigate('story', { id: story.id, slug: story.slug })}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    View
                  </button>
                  <button
                    onClick={() => setExpandedChapters(expandedChapters === story.id ? null : story.id)}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    {expandedChapters === story.id ? 'Hide Ch.' : 'Chapters'}
                  </button>
                  <button
                    onClick={() => setVolumeManagerStoryId(volumeManagerStoryId === story.id ? null : story.id)}
                    className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    {volumeManagerStoryId === story.id ? 'Hide Volumes' : 'Volumes'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ type: 'story', id: story.id, title: story.title })}
                    className="px-3 py-2 bg-red-900/50 hover:bg-red-800 text-red-400 hover:text-red-300 rounded-lg text-sm transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {editingStory === story.id && (
                <div className="border-t border-gray-800 p-5">
                  <h4 className="font-semibold mb-4">Edit Story Details</h4>
                  <div className="space-y-4">
                    <input
                      value={editForm.title}
                      onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Story Title"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
                    />
                    <textarea
                      value={editForm.synopsis}
                      onChange={e => setEditForm(f => ({ ...f, synopsis: e.target.value }))}
                      placeholder="Synopsis"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 h-32 resize-none focus:border-ink-500 focus:outline-none"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Genre</label>
                        <select
                          value={editForm.genre}
                          onChange={e => setEditForm(f => ({ ...f, genre: e.target.value }))}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none"
                        >
                          {['Action', 'Adventure', 'Comedy', 'Crime', 'Dark Fantasy', 'Drama', 'Dystopian', 'Erotica', 'Fanfiction', 'Fantasy', 'Historical', 'Horror', 'LGBTQ+', 'LitRPG', 'Mystery', 'New Adult', 'Paranormal', 'Poetry', 'Progression Fantasy', 'Romance', 'Romantasy', 'Sci-Fi', 'Short Story', 'Slice of Life', 'Thriller', 'Urban Fantasy', 'Werewolf', 'Western', 'Young Adult'].map(g => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Status</label>
                        <select
                          value={editForm.status}
                          onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none"
                        >
                          <option value="ONGOING">Ongoing</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="HIATUS">Hiatus</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Tags (comma-separated, max 8)</label>
                      <input
                        value={editForm.tags}
                        onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))}
                        placeholder="Tags (comma-separated)"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {editForm.tags ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean).length : 0}/8 tags
                      </p>
                    </div>
                    {penNames.length > 0 && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Publish as</label>
                        <select
                          value={editForm.penNameId}
                          onChange={e => setEditForm(f => ({ ...f, penNameId: e.target.value }))}
                          className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none w-full"
                        >
                          <option value="">{user?.username} (default)</option>
                          {penNames.map((pn: any) => (
                            <option key={pn.id} value={pn.id}>{pn.penName}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Cover Image (max 2MB)</label>
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleEditCoverFile}
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-ink-600 file:text-white file:text-sm file:cursor-pointer focus:outline-none"
                          />
                          {editCoverError && (
                            <p className="text-red-400 text-sm mt-1">{editCoverError}</p>
                          )}
                        </div>
                        {editCoverPreview && (
                          <div className="relative shrink-0">
                            <img src={editCoverPreview} alt="Cover preview" className="w-20 h-28 object-cover rounded-lg border border-gray-700" />
                            <button
                              type="button"
                              onClick={() => { setEditCoverPreview(null); setEditForm(f => ({ ...f, coverUrl: '' })); setEditCoverError(null); }}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`relative w-11 h-6 rounded-full transition-colors ${editForm.isMature ? 'bg-red-600' : 'bg-gray-700'}`}>
                        <input
                          type="checkbox"
                          checked={editForm.isMature}
                          onChange={e => setEditForm(f => ({ ...f, isMature: e.target.checked }))}
                          className="sr-only"
                        />
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${editForm.isMature ? 'translate-x-5' : ''}`} />
                      </div>
                      <span className={`text-sm font-medium ${editForm.isMature ? 'text-red-400' : 'text-gray-400'}`}>
                        18+ Content Warning
                      </span>
                    </label>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => { setEditingStory(null); setEditCoverPreview(null); setEditCoverError(null); }}
                        className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdateStory(story.id)}
                        disabled={!editForm.title || !editForm.synopsis || submitting}
                        className="px-6 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg font-medium disabled:opacity-50 transition-colors"
                      >
                        {submitting ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {analyticsStoryId === story.id && analytics && (
                <div className="border-t border-gray-800 p-5">
                  <div className="flex gap-1 mb-5 bg-gray-800/50 rounded-lg p-1">
                    {(['overview', 'traffic', 'demographics', 'chapters'] as const).map(tab => (
                      <button
                        key={tab}
                        onClick={() => setAnalyticsTab(tab)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
                          analyticsTab === tab ? 'bg-ink-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {analyticsTab === 'overview' && (
                    <div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-ink-400">{analytics.story?.totalViews || 0}</div>
                          <div className="text-xs text-gray-400">Total Views</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-emerald-400">{analytics.followCount}</div>
                          <div className="text-xs text-gray-400">Followers</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-amber-400">{analytics.reviewCount}</div>
                          <div className="text-xs text-gray-400">Reviews</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-purple-400">{analytics.score?.score?.toFixed(2) || '0.00'}</div>
                          <div className="text-xs text-gray-400">Score</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-cyan-400">{analytics.averageCompletionRate?.toFixed(1) || '0.0'}%</div>
                          <div className="text-xs text-gray-400">Avg Completion</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-pink-400">{analytics.engagementRate?.toFixed(2) || '0.00'}%</div>
                          <div className="text-xs text-gray-400">Engagement Rate</div>
                        </div>
                        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-orange-400">{analytics.chapterAnalytics?.length || 0}</div>
                          <div className="text-xs text-gray-400">Chapters</div>
                        </div>
                      </div>

                      {analytics.viewTimeline?.length > 0 && (() => {
                        const maxViews = Math.max(...analytics.viewTimeline.map((d: any) => d.views), 1);
                        return (
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold mb-3 text-gray-400">Views (Last 30 Days)</h4>
                            <div className="flex items-end gap-px h-32 bg-gray-800/30 rounded-lg p-2">
                              {analytics.viewTimeline.map((d: any, i: number) => (
                                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                                  <div
                                    className="w-full bg-ink-500/80 hover:bg-ink-400 rounded-t-sm transition-colors min-h-[2px]"
                                    style={{ height: `${Math.max((d.views / maxViews) * 100, 2)}%` }}
                                  />
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 border border-gray-700 text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                    {d.date}: {d.views} views
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-xs text-gray-600 mt-1 px-2">
                              <span>{analytics.viewTimeline[0]?.date?.slice(5)}</span>
                              <span>{analytics.viewTimeline[analytics.viewTimeline.length - 1]?.date?.slice(5)}</span>
                            </div>
                          </div>
                        );
                      })()}

                      {analytics.followGrowth?.length > 0 && (() => {
                        const growthByDate: Record<string, number> = {};
                        let cumulative = 0;
                        for (const f of analytics.followGrowth) {
                          const key = new Date(f.createdAt).toISOString().slice(0, 10);
                          growthByDate[key] = (growthByDate[key] || 0) + 1;
                        }
                        const entries = Object.entries(growthByDate).sort((a, b) => a[0].localeCompare(b[0]));
                        const cumulativeData = entries.map(([date, count]) => {
                          cumulative += count;
                          return { date, total: cumulative };
                        });
                        const maxFollows = Math.max(...cumulativeData.map(d => d.total), 1);
                        return (
                          <div>
                            <h4 className="text-sm font-semibold mb-3 text-gray-400">Follow Growth</h4>
                            <div className="flex items-end gap-px h-24 bg-gray-800/30 rounded-lg p-2">
                              {cumulativeData.map((d, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                                  <div
                                    className="w-full bg-emerald-500/80 hover:bg-emerald-400 rounded-t-sm transition-colors min-h-[2px]"
                                    style={{ height: `${Math.max((d.total / maxFollows) * 100, 2)}%` }}
                                  />
                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 border border-gray-700 text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                    {d.date}: {d.total} total
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {analyticsTab === 'traffic' && (
                    <div>
                      <h4 className="text-sm font-semibold mb-4 text-gray-400">Traffic Sources</h4>
                      {analytics.trafficSources?.length > 0 ? (() => {
                        const totalTraffic = analytics.trafficSources.reduce((s: number, t: any) => s + t.count, 0);
                        const sourceColors: Record<string, string> = { direct: 'bg-ink-500', social: 'bg-pink-500', search: 'bg-emerald-500', other: 'bg-amber-500' };
                        const sourceIcons: Record<string, string> = { direct: '🔗', social: '📱', search: '🔍', other: '🌐' };
                        return (
                          <div className="space-y-3">
                            {analytics.trafficSources.sort((a: any, b: any) => b.count - a.count).map((t: any) => {
                              const pct = totalTraffic > 0 ? (t.count / totalTraffic) * 100 : 0;
                              return (
                                <div key={t.source}>
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="text-gray-300 capitalize">{sourceIcons[t.source] || '🌐'} {t.source}</span>
                                    <span className="text-gray-400">{t.count} ({pct.toFixed(1)}%)</span>
                                  </div>
                                  <div className="w-full bg-gray-800 rounded-full h-3">
                                    <div
                                      className={`${sourceColors[t.source] || 'bg-gray-500'} h-3 rounded-full transition-all`}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            <div className="mt-4 pt-4 border-t border-gray-800 text-center">
                              <span className="text-sm text-gray-500">Total: {totalTraffic} views tracked</span>
                            </div>
                          </div>
                        );
                      })() : (
                        <p className="text-gray-500 text-sm text-center py-6">No traffic data available yet</p>
                      )}
                    </div>
                  )}

                  {analyticsTab === 'demographics' && (
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-gray-400">Country Distribution</h4>
                        {analytics.demographics?.country?.length > 0 ? (() => {
                          const maxCount = Math.max(...analytics.demographics.country.map((d: any) => d.count), 1);
                          return (
                            <div className="space-y-2">
                              {analytics.demographics.country.slice(0, 10).map((d: any) => (
                                <div key={d.country}>
                                  <div className="flex items-center justify-between text-sm mb-0.5">
                                    <span className="text-gray-300">{d.country}</span>
                                    <span className="text-gray-500">{d.count}</span>
                                  </div>
                                  <div className="w-full bg-gray-800 rounded-full h-2">
                                    <div
                                      className="bg-cyan-500 h-2 rounded-full transition-all"
                                      style={{ width: `${(d.count / maxCount) * 100}%` }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })() : (
                          <p className="text-gray-500 text-sm text-center py-4">No country data available</p>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-gray-400">Gender Split</h4>
                        {analytics.demographics?.gender?.length > 0 ? (() => {
                          const total = analytics.demographics.gender.reduce((s: number, d: any) => s + d.count, 0);
                          const genderColors: Record<string, string> = { Male: 'bg-blue-500', Female: 'bg-pink-500', 'Non-binary': 'bg-purple-500', Other: 'bg-amber-500', Unknown: 'bg-gray-600' };
                          return (
                            <div>
                              <div className="flex rounded-lg overflow-hidden h-6 mb-3">
                                {analytics.demographics.gender.map((d: any) => (
                                  <div
                                    key={d.gender}
                                    className={`${genderColors[d.gender] || 'bg-gray-600'} flex items-center justify-center text-xs font-medium`}
                                    style={{ width: `${(d.count / total) * 100}%` }}
                                    title={`${d.gender}: ${d.count}`}
                                  >
                                    {(d.count / total) * 100 > 10 ? `${((d.count / total) * 100).toFixed(0)}%` : ''}
                                  </div>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-3">
                                {analytics.demographics.gender.map((d: any) => (
                                  <div key={d.gender} className="flex items-center gap-1.5 text-xs text-gray-400">
                                    <div className={`w-2.5 h-2.5 rounded-full ${genderColors[d.gender] || 'bg-gray-600'}`} />
                                    <span>{d.gender}: {d.count} ({((d.count / total) * 100).toFixed(1)}%)</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })() : (
                          <p className="text-gray-500 text-sm text-center py-4">No gender data available</p>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-gray-400">Age Range Distribution</h4>
                        {analytics.demographics?.ageRange?.length > 0 ? (() => {
                          const maxCount = Math.max(...analytics.demographics.ageRange.map((d: any) => d.count), 1);
                          return (
                            <div className="space-y-2">
                              {analytics.demographics.ageRange.map((d: any) => (
                                <div key={d.ageRange}>
                                  <div className="flex items-center justify-between text-sm mb-0.5">
                                    <span className="text-gray-300">{d.ageRange}</span>
                                    <span className="text-gray-500">{d.count}</span>
                                  </div>
                                  <div className="w-full bg-gray-800 rounded-full h-2">
                                    <div
                                      className="bg-orange-500 h-2 rounded-full transition-all"
                                      style={{ width: `${(d.count / maxCount) * 100}%` }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })() : (
                          <p className="text-gray-500 text-sm text-center py-4">No age data available</p>
                        )}
                      </div>
                    </div>
                  )}

                  {analyticsTab === 'chapters' && (
                    <div>
                      {analytics.chapterAnalytics?.length > 0 ? (
                        <div>
                          <h4 className="text-sm font-semibold mb-3 text-gray-400">Chapter Performance</h4>
                          <div className="space-y-1.5">
                            {analytics.chapterAnalytics.map((ch: any) => (
                              <div key={ch.id} className="flex items-center gap-3 text-sm">
                                <span className="text-gray-500 w-10 shrink-0">Ch.{ch.chapterNum}</span>
                                <span className="flex-1 text-gray-300 truncate">{stripChapterPrefix(ch.title)}</span>
                                <span className="text-gray-500 shrink-0">{ch.totalViews} views</span>
                                <div className="w-24 bg-gray-800 rounded-full h-2 shrink-0">
                                  <div
                                    className="bg-ink-500 h-2 rounded-full"
                                    style={{ width: `${ch.completionRate}%` }}
                                  />
                                </div>
                                <span className="text-gray-500 w-12 text-right shrink-0">{ch.completionRate.toFixed(0)}%</span>
                              </div>
                            ))}
                          </div>

                          {analytics.completionFunnel?.length > 1 && (() => {
                            const maxReaders = Math.max(...analytics.completionFunnel.map((d: any) => d.uniqueReaders), 1);
                            return (
                              <div className="mt-6">
                                <h4 className="text-sm font-semibold mb-3 text-gray-400">Reader Retention Funnel</h4>
                                <div className="space-y-1">
                                  {analytics.completionFunnel.map((d: any) => {
                                    const pct = (d.uniqueReaders / maxReaders) * 100;
                                    return (
                                      <div key={d.chapterNum} className="flex items-center gap-3 text-sm">
                                        <span className="text-gray-500 w-10 shrink-0">Ch.{d.chapterNum}</span>
                                        <div className="flex-1 bg-gray-800 rounded-full h-4 overflow-hidden">
                                          <div
                                            className="bg-gradient-to-r from-emerald-600 to-emerald-400 h-4 rounded-full flex items-center justify-end pr-2 transition-all"
                                            style={{ width: `${Math.max(pct, 5)}%` }}
                                          >
                                            {pct > 15 && <span className="text-xs font-medium">{d.uniqueReaders}</span>}
                                          </div>
                                        </div>
                                        {pct <= 15 && <span className="text-gray-500 text-xs shrink-0">{d.uniqueReaders} readers</span>}
                                        <span className="text-gray-600 text-xs w-12 text-right shrink-0">{pct.toFixed(0)}%</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-6">No chapter data available yet</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {followersStoryId === story.id && (
                <div className="border-t border-gray-800 p-5">
                  <h4 className="text-sm font-semibold mb-3 text-gray-400">Story Followers</h4>
                  {followersLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="w-5 h-5 border-2 border-ink-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : followers.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No followers yet</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {followers.map((f: any) => (
                        <button
                          key={f.id}
                          onClick={() => onNavigate('profile', { username: f.username })}
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-ink-600/30 flex items-center justify-center text-xs font-bold shrink-0">
                            {f.avatarUrl ? (
                              <img src={f.avatarUrl} alt={f.username} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              f.username[0].toUpperCase()
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-200">{f.username}</span>
                            <span className="text-xs text-gray-500 ml-2">Lv.{f.level}</span>
                          </div>
                          <span className="text-xs text-gray-500">{new Date(f.followedAt).toLocaleDateString()}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {volumeManagerStoryId === story.id && (
                <div className="border-t border-gray-800 p-5">
                  <h4 className="text-sm font-semibold mb-3 text-gray-400">Volumes &amp; Story Arcs</h4>
                  <VolumeManager
                    storyId={story.id}
                    allChapters={story.chapters || []}
                    onUpdate={async () => {
                      const refreshed = await api.dashboard.stories();
                      setStories(refreshed);
                    }}
                  />
                </div>
              )}

              {expandedChapters === story.id && story.chapters?.length > 0 && (
                <div className="border-t border-gray-800 p-5">
                  <h4 className="text-sm font-semibold mb-3 text-gray-400">Chapters ({story.chapters.length}) — drag to reorder</h4>
                  <div className="space-y-1">
                    {story.chapters.sort((a: any, b: any) => a.chapterNum - b.chapterNum).map((ch: any) => (
                      <div
                        key={ch.id}
                        draggable
                        onDragStart={(e) => {
                          setDragChapterId(ch.id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          if (dragChapterId === null || dragChapterId === ch.id) return;
                          const sorted = [...story.chapters].sort((a: any, b: any) => a.chapterNum - b.chapterNum);
                          const dragIdx = sorted.findIndex((c: any) => c.id === dragChapterId);
                          const dropIdx = sorted.findIndex((c: any) => c.id === ch.id);
                          if (dragIdx === -1 || dropIdx === -1) return;
                          const [moved] = sorted.splice(dragIdx, 1);
                          sorted.splice(dropIdx, 0, moved);
                          const reordered = sorted.map((c: any, i: number) => ({ ...c, chapterNum: i + 1 }));
                          setStories(prev => prev.map(s => s.id === story.id ? { ...s, chapters: reordered } : s));
                          setDragChapterId(null);
                          try {
                            await api.chapters.reorder({
                              storyId: story.id,
                              chapters: reordered.map((c: any) => ({ id: c.id, chapterNum: c.chapterNum }))
                            });
                            const refreshed = await api.dashboard.stories();
                            setStories(refreshed);
                          } catch (err) {
                            console.error('Reorder failed:', err);
                            const refreshed = await api.dashboard.stories();
                            setStories(refreshed);
                          }
                        }}
                        onDragEnd={() => setDragChapterId(null)}
                        className={`flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-gray-800/50 group cursor-grab active:cursor-grabbing ${dragChapterId === ch.id ? 'opacity-50 bg-gray-800/30' : ''}`}
                      >
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-gray-600 select-none">⠿</span>
                          <span className="text-gray-500 w-10">Ch.{ch.chapterNum}</span>
                          <span className="text-gray-300">{stripChapterPrefix(ch.title)}</span>
                          <span className="text-gray-600 text-xs">{ch.wordCount} words</span>
                          {ch.isDraft && !ch.scheduledAt && (
                            <span className="px-1.5 py-0.5 bg-yellow-900/30 text-yellow-400 rounded-full text-xs font-medium">Draft</span>
                          )}
                          {ch.scheduledAt && new Date(ch.scheduledAt) > new Date() && (
                            <span className="px-1.5 py-0.5 bg-amber-900/30 text-amber-400 rounded-full text-xs font-medium">
                              🕐 {new Date(ch.scheduledAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEditChapter(ch.id, story.id)}
                            className="px-3 py-1 text-ink-400 hover:bg-ink-600/20 rounded text-xs font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ type: 'chapter', id: ch.id, title: `Ch.${ch.chapterNum}: ${stripChapterPrefix(ch.title)}` })}
                            className="px-2 py-1 text-red-400 hover:bg-red-900/30 rounded text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showNewChapter === story.id && (() => {
                const isEditing = editingChapter && editingChapter.storyId === story.id;
                const canSubmit = chapterForm.title && chapterForm.content && !submitting && (!chapterForm.isScheduled || (chapterForm.scheduledAt && new Date(chapterForm.scheduledAt) > new Date()));
                const handleSubmit = isEditing ? handleUpdateChapter : handleCreateChapter;
                const submitLabel = isEditing
                  ? (chapterForm.isDraft ? 'Save Draft' : 'Save & Publish')
                  : (chapterForm.isDraft ? 'Save as Draft' : (chapterForm.isScheduled ? 'Schedule' : 'Publish'));

                const actionBar = (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{chapterWordCount} words</span>
                    <div className="flex gap-2">
                      <button onClick={() => { setShowNewChapter(null); setEditingChapter(null); setChapterForm({ title: '', content: '', authorNote: '', isScheduled: false, scheduledAt: '', isDraft: false }); }} className="px-4 py-2 text-gray-400 hover:text-white text-sm">
                        Cancel
                      </button>
                      <button
                        onClick={() => isEditing ? handleUpdateChapter(true) : handleCreateChapter(true)}
                        disabled={!chapterForm.title || !chapterForm.content || submitting}
                        className="px-4 py-2 bg-yellow-900/40 hover:bg-yellow-800/50 text-yellow-400 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                      >
                        Save as Draft
                      </button>
                      <button
                        onClick={() => isEditing ? handleUpdateChapter(false) : handleCreateChapter(false)}
                        disabled={!canSubmit}
                        className="px-5 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
                      >
                        {isEditing ? 'Save & Publish' : (chapterForm.isScheduled ? 'Schedule' : 'Publish')}
                      </button>
                    </div>
                  </div>
                );

                return (
                  <div className="border-t border-gray-800 p-5">
                    <h4 className="font-semibold mb-3">
                      {isEditing ? 'Edit Chapter' : 'New Chapter'}
                      {chapterForm.isDraft && <span className="ml-2 px-1.5 py-0.5 bg-yellow-900/30 text-yellow-400 rounded-full text-xs font-medium">Draft</span>}
                    </h4>
                    {actionBar}
                    <div className="mt-4">
                      <input
                        value={chapterForm.title}
                        onChange={e => setChapterForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="Chapter Title"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 mb-3 focus:border-ink-500 focus:outline-none"
                      />
                      <RichTextEditor
                        value={chapterForm.content}
                        onChange={html => setChapterForm(f => ({ ...f, content: html }))}
                        placeholder="Begin writing your chapter..."
                        minHeight="300px"
                      />
                      <input
                        value={chapterForm.authorNote}
                        onChange={e => setChapterForm(f => ({ ...f, authorNote: e.target.value }))}
                        placeholder="Author's note (optional)"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 mt-3 text-sm focus:border-ink-500 focus:outline-none"
                      />
                      <div className="flex items-center gap-4 mt-3">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name={`publish-mode-${story.id}`}
                            checked={!chapterForm.isScheduled}
                            onChange={() => setChapterForm(f => ({ ...f, isScheduled: false, scheduledAt: '' }))}
                            className="accent-ink-500"
                          />
                          <span className={!chapterForm.isScheduled ? 'text-white' : 'text-gray-400'}>Publish Now</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name={`publish-mode-${story.id}`}
                            checked={chapterForm.isScheduled}
                            onChange={() => setChapterForm(f => ({ ...f, isScheduled: true }))}
                            className="accent-ink-500"
                          />
                          <span className={chapterForm.isScheduled ? 'text-white' : 'text-gray-400'}>Schedule for Later</span>
                        </label>
                      </div>
                      {chapterForm.isScheduled && (
                        <div className="mt-2">
                          <input
                            type="datetime-local"
                            value={chapterForm.scheduledAt}
                            onChange={e => setChapterForm(f => ({ ...f, scheduledAt: e.target.value }))}
                            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-sm focus:border-ink-500 focus:outline-none"
                          />
                          {chapterForm.scheduledAt && new Date(chapterForm.scheduledAt) <= new Date() && (
                            <p className="text-red-400 text-xs mt-1">Scheduled date must be in the future</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="mt-4">{actionBar}</div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-2">Delete {deleteConfirm.type === 'story' ? 'Story' : 'Chapter'}</h3>
            <p className="text-gray-400 mb-1">Are you sure you want to delete:</p>
            <p className="font-semibold mb-4 text-white">{deleteConfirm.title}</p>
            {deleteConfirm.type === 'story' && (
              <p className="text-red-400 text-sm mb-4">This will permanently delete all chapters, reviews, followers, and other data associated with this story.</p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={submitting}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="px-5 py-2 bg-red-600 hover:bg-red-500 rounded-lg font-medium disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
